import React, { useEffect, useState } from "react";

import {
  useStepperDispatch,
  TextArea,
  Typography,
  InfoPanel,
  RadioButton,
  RadioGroup,
} from "@king-ict/eupisi-ui/components";
import { nameof, isEmpty } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getIntegrationCriterionData } from "../../../api/kindergartenApi";
import { RootState } from "../../../app/rootReducer";
import { useAppDispatch } from "../../../app/store";
import useInput from "../../../hooks/input/useInput";
import CriterionFetchingParameters from "../../../models/Criterion/CriterionFetchingParameters";
import RequestCriterion from "../../../models/Request/RequestCriterion";
import RequestCriterionData, {
  requestCriterionDataInitial,
} from "../../../models/Request/RequestCriterionData";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import {
  selectAllDocuments,
  updateRequestCriterionData,
  getPersonsOibs,
  updateRequestCriterionDataItem,
} from "../../../slices/requestSlice";
import styles from "./CriteriaStep.module.scss";
import CriterionItem from "./CriterionItem";

export default function CriteriaStep() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [criterionData, setCriterionData] = useState<RequestCriterionData>(
    requestCriterionDataInitial
  );

  const [
    documentsModalOpenId,
    setDocumentsModalOpenId,
  ] = React.useState<number>();

  const allDocuments = useSelector(selectAllDocuments);
  const personsOibs = useSelector(getPersonsOibs);
  const criterionDataState = useSelector(
    (state: RootState) => state.request.request.criterionData
  );

  const [criteriaErrors, setCriteriaErrors] = useState<number[]>([]);

  const getCriterionDocumentsById = (id: number) => {
    const criterionDocuments: RequestDocumentData[] = [];

    if (criterionData !== undefined) {
      criterionData.criteria?.forEach((requestCriteria) => {
        if (requestCriteria.institutionCriterionId === id) {
          requestCriteria.documents?.forEach((d) => {
            criterionDocuments.push(d);
          });
        }
      });
    }
    return criterionDocuments;
  };

  const onCloseDocumentsModal = () => {
    setDocumentsModalOpenId(undefined);
  };

  const criteria = useSelector((state: RootState) => state.request.criteria);

  useEffect(() => {
    if (!isEmpty(criterionDataState)) {
      setCriterionData(criterionDataState);
      checkErrorsForAllCriteria(
        criterionDataState.criteria,
        criterionDataState.isConsentAccepted
      );
    }
  }, [criterionDataState]);

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleNextStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      dispatch(updateRequestCriterionData(criterionData));
    },
  });

  useStepperDispatch({
    type: "save",
    payload: () => {
      dispatch(updateRequestCriterionData(criterionData));
    },
  });

  const isConsentAcceptedIsDefined =
    criterionData.isConsentAccepted !== undefined &&
    criterionData.isConsentAccepted !== null;

  const handleNextStep = () => {
    if (isConsentAcceptedIsDefined === false) {
      return false;
    }
    if (!criteriaErrors || criteriaErrors.length === 0) {
      let integrationCriteria = new Array<CriterionFetchingParameters>();
      let fetchedIntegration = true;

      if (criterionData.criteria) {
        fetchedIntegration =
          criterionData.criteria.filter(
            (c) => c.integrationId && !c.fetchedIntegrationData
          ).length === 0;

        if (!fetchedIntegration && criterionData.isConsentAccepted) {
          criterionData.criteria.forEach((criterion) => {
            if (criterion.integrationId) {
              integrationCriteria.push({
                criterionId: criterion.criterionId,
                applicantParentOib: personsOibs.applicantParentOib,
                otherParentOib: personsOibs.otherParentOib,
                childOib: personsOibs.childOib,
              } as CriterionFetchingParameters);
            }
          });

          if (integrationCriteria.length > 0) {
            getIntegrationCriterionData(integrationCriteria).then((res) => {
              const newCriterionData = criterionData.criteria.map(
                (prevCriterion) => {
                  const integrationResult = res.find(
                    (r) => r.criterionId === prevCriterion.criterionId
                  );
                  if (integrationResult) {
                    return {
                      ...prevCriterion,
                      integrationData: integrationResult,
                      fetchedIntegrationData: true,
                    };
                  }
                  return prevCriterion;
                }
              );

              setCriterionData((prev) => ({
                ...prev,
                criteria: newCriterionData,
              }));

              dispatch(
                updateRequestCriterionData({
                  ...criterionData,
                  criteria: newCriterionData,
                })
              );
            });

            return false;
          }
        } else {
          dispatch(updateRequestCriterionData(criterionData));
        }
      }
    } else {
      return false;
    }

    const criteriaThatHasNotBeenMarkedByUser = checkCriteriaHasBeenMarkedByUser(
      criteria
    );
    if (criteriaThatHasNotBeenMarkedByUser.length !== 0) {
      return false;
    }
  };

  const handleSelectChange = (
    criterion: RequestCriterion,
    isSelected: boolean
  ) => {
    setCriterionData((prev) => {
      criterion = { ...criterion, hasBeenMarkedByUser: true };
      const newCriteria = isSelected
        ? prev.criteria
          ? [...prev.criteria, criterion]
          : [criterion]
        : prev.criteria?.filter(
            (c) => c.institutionCriterionId !== criterion.institutionCriterionId
          );

      checkErrors(criterion, isSelected, criterionData.isConsentAccepted);
      dispatch(updateRequestCriterionData({ ...prev, criteria: newCriteria }));
      dispatch(updateRequestCriterionDataItem(criterion));
      return { ...prev, criteria: newCriteria };
    });
  };

  const handleConsentAcceptedChanged = (isConsentAccepted: boolean) => {
    setCriterionData((prev) => ({
      ...prev,
      isConsentAccepted: isConsentAccepted,
    }));

    checkErrorsForAllCriteria(criterionData.criteria, isConsentAccepted);
  };

  const checkErrors = (
    criterion: RequestCriterion,
    isSelected: boolean,
    isConsentAccepted: boolean | undefined
  ) => {
    if (isSelected && !isConsentAccepted && criterion.documents.length === 0) {
      if (!checkIfCriteriaHasError(criterion)) {
        criteriaErrors.push(criterion.criterionId);
      }
    } else if (
      isSelected &&
      isConsentAccepted &&
      !criterion.integrationId &&
      criterion.documents.length === 0
    ) {
      if (!checkIfCriteriaHasError(criterion)) {
        criteriaErrors.push(criterion.criterionId);
      }
    } else {
      if (checkIfCriteriaHasError(criterion)) {
        const newCriterionErrors = criteriaErrors.filter(
          (x) => x !== criterion.criterionId
        );
        setCriteriaErrors(newCriterionErrors);
      }
    }
  };

  const checkErrorsForAllCriteria = (
    criterion: RequestCriterion[],
    isConsentAccepted: boolean | undefined
  ) => {
    const tempcriteriaErrors: number[] = [];
    criterion.forEach((c) => {
      if (!isConsentAccepted && (!c.documents || c.documents.length === 0)) {
        tempcriteriaErrors.push(c.criterionId);
      } else if (
        isConsentAccepted &&
        !c.integrationId &&
        (!c.documents || c.documents.length === 0)
      ) {
        tempcriteriaErrors.push(c.criterionId);
      }
      if (c.hasBeenMarkedByUser === false) {
        tempcriteriaErrors.push(c.criterionId);
      }
    });
    setCriteriaErrors(tempcriteriaErrors);
  };

  const checkCriteriaHasBeenMarkedByUser = (criterion: RequestCriterion[]) => {
    const tempcriteriaErrors: number[] = [];
    criterion.forEach((c) => {
      if (
        c.hasBeenMarkedByUser === false &&
        criterionDataState.criteria.some(
          (criteria) =>
            criteria.institutionCriterionId === c.institutionCriterionId
        ) === false
      ) {
        tempcriteriaErrors.push(c.criterionId);
      }
    });
    return tempcriteriaErrors;
  };

  const checkIfCriteriaHasError = (criterion: RequestCriterion) => {
    return criteriaErrors.some((x) => x === criterion.criterionId);
  };

  const handleDocumentsChange = (
    criterion: RequestCriterion,
    documents: RequestDocumentData[]
  ) => {
    setCriterionData((prev) => {
      const tempCriteria = prev.criteria ? [...prev.criteria] : [];
      const newCriteria: RequestCriterion[] = [];
      tempCriteria.forEach((cr) => {
        if (cr.institutionCriterionId === criterion.institutionCriterionId) {
          newCriteria.push({
            ...cr,
            documents: cr.documents
              ? [...cr.documents, ...documents]
              : documents,
          });
        } else {
          newCriteria.push({ ...cr });
        }
      });
      const newCrt = newCriteria.find(
        (x) => x.criterionId === criterion.criterionId
      );
      if (newCrt) {
        checkErrors(newCrt, true, criterionData.isConsentAccepted);
      }
      return { ...prev, criteria: newCriteria };
    });
  };

  const handleConfirmDocumentsModal = (
    criterion: RequestCriterion,
    documents: RequestDocumentData[]
  ) => {
    handleDocumentsChange(criterion, documents);
  };

  const handleRemoveDocumentFromList = (
    criterion: RequestCriterion,
    document: RequestDocumentData
  ) => {
    const findCirerionData = criterionData.criteria.find(
      (cr) => cr.institutionCriterionId === criterion.institutionCriterionId
    );
    const newDocuments: RequestDocumentData[] = findCirerionData?.documents
      ? [...findCirerionData?.documents]
      : [];

    var index = [...newDocuments].indexOf(document);

    if (index !== -1) {
      newDocuments.splice(index, 1);

      setCriterionData((prev) => {
        const tempCriteria = prev.criteria ? [...prev.criteria] : [];
        const newCriteria: RequestCriterion[] = [];
        tempCriteria.forEach((cr) => {
          if (cr.institutionCriterionId === criterion.institutionCriterionId) {
            newCriteria.push({ ...cr, documents: newDocuments });
          } else {
            newCriteria.push({ ...cr });
          }
        });

        dispatch(
          updateRequestCriterionData({ ...prev, criteria: newCriteria })
        );

        const newCrt = newCriteria.find(
          (x) => x.criterionId === criterion.criterionId
        );
        if (newCrt) {
          checkErrors(newCrt, true, criterionData.isConsentAccepted);
        }

        return { ...prev, criteria: newCriteria };
      });
    }
  };

  const checkIfCriterionExistsInState = (criterion: RequestCriterion) => {
    return criterionData.criteria?.some(
      (c) => c.institutionCriterionId === criterion.institutionCriterionId
    );
  };

  const totalPoints = criterionData.criteria?.reduce(
    (points, criterion) => points + criterion.points,
    0
  );
  const isDirectEnrollment = criterionData.criteria?.some(
    (x) => x.isDirectEnrollment
  );

  const getCriterionData = (initalCriterion: RequestCriterion) => {
    if (
      initalCriterion.integrationId &&
      checkIfCriterionExistsInState(initalCriterion)
    ) {
      const selectedCriterion = criterionData.criteria.find(
        (c) =>
          c.institutionCriterionId === initalCriterion.institutionCriterionId
      );
      if (selectedCriterion && selectedCriterion.integrationData) {
        return {
          ...initalCriterion,
          integrationData: { ...selectedCriterion.integrationData },
          fetchedIntegrationData: selectedCriterion.fetchedIntegrationData,
          hasBeenMarkedByUser: true,
        } as RequestCriterion;
      }
    }

    if (checkIfCriterionExistsInState(initalCriterion)) {
      return {
        ...initalCriterion,
        hasBeenMarkedByUser: true,
      };
    }

    return initalCriterion;
  };

  return (
    <>
      <Row>
        <Col xs={12} className={styles.bottomSpacing}>
          <InfoPanel
            message={t("criteriaStep.mainInfoMessage")}
            color="success"
            fullWidth
          />
        </Col>
        <Col xs={12} className={styles.bottomSpacing}>
          <Typography component="h3" variant="smallHeading">
            {t("criteriaStep.header")}
          </Typography>
        </Col>
        <Col xs={12} className={styles.bottomSpacing}>
          <RadioGroup
            name={nameof<typeof criterionData>("isConsentAccepted")}
            title={t("criteriaStep.consentText")}
            required={false}
            onChange={(e) =>
              handleConsentAcceptedChanged(e.target.value === "1")
            }
            errorText={
              isConsentAcceptedIsDefined === false
                ? "Molimo odaberite."
                : undefined
            }
          >
            <br />
            <div style={{ display: "inline-flex" }}>
              <RadioButton
                key={"Da"}
                label={"Da"}
                value={1}
                checked={
                  isConsentAcceptedIsDefined &&
                  criterionData.isConsentAccepted === true
                }
                ariaLabel={t("accessibility.radioAnswer")}
              />
              <div style={{ marginLeft: "1em" }}></div>
              <RadioButton
                key={"Ne"}
                label={"Ne"}
                value={0}
                checked={
                  isConsentAcceptedIsDefined &&
                  criterionData.isConsentAccepted === false
                }
                ariaLabel={t("accessibility.radioAnswer")}
              />
            </div>
          </RadioGroup>
        </Col>
        <Col xs={12} className={styles.bottomSpacing}>
          <hr />
        </Col>
      </Row>

      {isConsentAcceptedIsDefined &&
        criteria.map((c) => (
          <CriterionItem
            key={c.institutionCriterionId}
            consentAccepted={
              isConsentAcceptedIsDefined &&
              criterionData.isConsentAccepted === true
            }
            selected={checkIfCriterionExistsInState(c)}
            criterion={getCriterionData(c)}
            criterionDocuments={getCriterionDocumentsById(
              c.institutionCriterionId
            )}
            onSelect={(criterion, isSelected) =>
              handleSelectChange(criterion, isSelected)
            }
            onDocumentsChange={(criterion, documents) =>
              handleConfirmDocumentsModal(criterion, documents)
            }
            onRemoveDocumentFromList={(criterion, document) =>
              handleRemoveDocumentFromList(criterion, document)
            }
            documentsModalOpenId={documentsModalOpenId}
            setDocumentsModalOpenId={setDocumentsModalOpenId}
            onCloseDocumentModal={onCloseDocumentsModal}
            allDocuments={allDocuments}
            criterionError={checkIfCriteriaHasError(c)}
          />
        ))}
      <Row>
        <Col xs={12}>
          <Typography
            className={styles.possiblePointsText}
            variant="XLargeBold"
          >
            {isDirectEnrollment
              ? t("criteriaStep.directEnrollmentPossible")
              : t("criteriaStep.possiblePoints", { points: totalPoints })}
          </Typography>
          <Typography className={styles.bottomSpacing} color="light">
            {t("criteriaStep.possiblePointsHelp")}
          </Typography>
          <TextArea
            name={nameof<typeof criterionData>("comment")}
            value={criterionData.comment ?? ""}
            label={t("criteriaStep.comment")}
            maxLength={700}
            rows={4}
            onChange={(e) => handleChange(e, setCriterionData)}
            ariaLabel={t("criteriaStep.comment")}
          />
        </Col>
      </Row>
    </>
  );
}
